import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, ChevronLeft, ChevronRight, Pencil, Plus, Trash2, UserCog } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const KindBadge = ({ kind, category }) => {
  if (kind === "holiday") return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Holiday</Badge>;
  if (kind === "event") return <Badge variant="outline" className="bg-blue-50 text-[#002FA7] border-blue-200">Event</Badge>;
  return category === "teacher"
    ? <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Teacher deadline</Badge>
    : <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Student deadline</Badge>;
};

export default function Events() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", date: "", kind: "event", category: "student", description: "", scope: "all", department_id: "", semester_id: "" });
  const [managersOpen, setManagersOpen] = useState(false);
  const [deptFilter, setDeptFilter] = useState("all");

  const { data: events = [] } = useQuery({
    queryKey: ["events", deptFilter],
    queryFn: async () => (await api.get("/events", { params: deptFilter !== "all" ? { department_id: deptFilter } : {} })).data,
  });
  const { data: departments = [] } = useQuery({
    queryKey: ["departments"],
    queryFn: async () => (await api.get("/admin/departments")).data,
    enabled: user?.role === "admin",
  });
  const { data: semesters = [] } = useQuery({
    queryKey: ["semesters"],
    queryFn: async () => (await api.get("/admin/semesters")).data,
    enabled: user?.role === "admin",
  });
  const { data: teacherSubjects = [] } = useQuery({
    queryKey: ["teacher-subjects"],
    queryFn: async () => (await api.get("/teacher/subjects")).data,
    enabled: user?.role === "teacher",
  });
  const { data: marks } = useQuery({
    queryKey: ["calendar-marks"],
    queryFn: async () => (await api.get("/staff/calendar-marks")).data,
  });
  const holidayMap = {};
  (marks?.holidays || []).forEach((h) => { holidayMap[h.date] = h.name; });
  const weeklyOffs = new Set(marks?.weekly_offs ?? [6]);
  const { data: managers = [] } = useQuery({
    queryKey: ["event-managers"],
    queryFn: async () => (await api.get("/events/managers")).data,
    enabled: user?.role === "admin",
  });

  const canManage = user?.role === "admin" || !!user?.event_manager;
  const canHoliday = user?.role === "admin" || (user?.role === "teacher" && !!user?.is_hod);
  const { data: holidays = [] } = useQuery({
    queryKey: ["holidays"],
    queryFn: async () => (await api.get("/staff/admin/holidays")).data,
    enabled: canHoliday,
  });

  const shiftMonth = (delta) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const openCreate = () => { setEditing(null); setForm({ title: "", date: "", kind: canManage ? "event" : "holiday", category: "student", description: "", scope: "all", department_id: user?.department_id || "", semester_id: "" }); setDialogOpen(true); };
  const openEdit = (ev) => { setEditing(ev); setForm({ title: ev.title, date: ev.date, kind: ev.kind, category: ev.category, description: ev.description, scope: ev.scope || "all", department_id: ev.department_id || "", semester_id: ev.semester_id || "" }); setDialogOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      department_id: form.scope === "department" ? (form.department_id || user?.department_id) : null,
      semester_id: form.scope === "semester" ? form.semester_id : null,
    };
    try {
      if (form.kind === "holiday") {
        await api.post("/staff/admin/holidays", {
          date: form.date, name: form.title,
          department_id: user?.role === "admin" && form.scope === "department" ? form.department_id : null,
        });
        toast.success("Holiday added");
        setDialogOpen(false);
        queryClient.invalidateQueries({ queryKey: ["holidays"] });
        queryClient.invalidateQueries({ queryKey: ["calendar-marks"] });
        return;
      }
      if (editing) {
        await api.put(`/events/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/events", payload);
        toast.success("Added to calendar");
      }
      setDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["events"] });
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/events/${id}`);
      toast.success("Deleted");
      queryClient.invalidateQueries({ queryKey: ["events"] });
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const removeHoliday = async (id) => {
    try {
      await api.delete(`/staff/admin/holidays/${id}`);
      toast.success("Holiday removed");
      queryClient.invalidateQueries({ queryKey: ["holidays"] });
      queryClient.invalidateQueries({ queryKey: ["calendar-marks"] });
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const toggleManager = async (tid, val) => {
    try {
      await api.put(`/events/managers/${tid}`, { can_manage: val });
      queryClient.invalidateQueries({ queryKey: ["event-managers"] });
      toast.success(val ? "Teacher assigned to events section" : "Teacher unassigned");
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  // Calendar grid
  const first = new Date(viewYear, viewMonth, 1);
  const startOffset = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const monthKey = `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}`;
  const eventsOn = (day) => events.filter((e) => e.date === `${monthKey}-${String(day).padStart(2, "0")}`);
  const todayStr = today.toISOString().slice(0, 10);
  const upcoming = [
    ...events,
    ...holidays.map((h) => ({ id: h.id, title: h.name, date: h.date, kind: "holiday", category: "all",
      description: "", scope: h.department_id ? "department" : "all", department_id: h.department_id })),
  ].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <Layout title="Events & Deadlines">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-slate-500">
          Events and student deadlines are visible to everyone. Teacher deadlines are visible to teachers and admin only.
        </p>
        <div className="flex gap-2 items-center">
          {user?.role === "admin" && departments.length > 0 && (
            <div className="w-52">
              <Select value={deptFilter} onValueChange={setDeptFilter}>
                <SelectTrigger data-testid="events-dept-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All departments</SelectItem>
                  {departments.map((d) => <SelectItem key={d.id} value={d.id} data-testid={`events-dept-${d.id}`}>{d.name} calendar</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {user?.role === "admin" && (
            <Button variant="outline" size="sm" data-testid="assign-managers-btn" onClick={() => setManagersOpen(true)}>
              <UserCog className="h-4 w-4" /> Assign teachers
            </Button>
          )}
          {(canManage || canHoliday) && (
            <Button size="sm" data-testid="add-event-btn" onClick={openCreate} className="bg-slate-950 hover:bg-slate-800 text-white">
              <Plus className="h-4 w-4" /> {canManage ? "Add event / deadline" : "Add holiday"}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="events-calendar">
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" size="icon" data-testid="cal-prev" onClick={() => shiftMonth(-1)}><ChevronLeft className="h-4 w-4" /></Button>
            <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950">{MONTHS[viewMonth]} {viewYear}</h2>
            <Button variant="ghost" size="icon" data-testid="cal-next" onClick={() => shiftMonth(1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center">
            {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
              <p key={d} className="text-xs text-slate-400 py-1 font-medium">{d}</p>
            ))}
            {cells.map((day, i) => {
              if (day === null) return <div key={`e${i}`} />;
              const evs = eventsOn(day);
              const dateStr = `${monthKey}-${String(day).padStart(2, "0")}`;
              const isToday = dateStr === todayStr;
              const holidayName = holidayMap[dateStr];
              const jsDay = new Date(viewYear, viewMonth, day).getDay();
              const isOff = weeklyOffs.has((jsDay + 6) % 7); // backend: 0=Mon..6=Sun
              const isRed = !!holidayName || isOff;
              return (
                <div key={day} data-testid={`cal-day-${monthKey}-${String(day).padStart(2, "0")}`}
                  title={holidayName || (isOff ? "Weekly off" : undefined)}
                  className={`min-h-14 rounded-md border p-1 text-left ${
                    isRed ? "border-red-200 bg-red-50" : isToday ? "border-slate-950 bg-slate-50" : "border-slate-100"}`}>
                  <p className={`text-xs font-data ${isRed ? "font-semibold text-red-700" : isToday ? "font-bold text-slate-950" : "text-slate-500"}`}>
                    {day}
                  </p>
                  <div className="space-y-0.5 mt-0.5">
                    {evs.slice(0, 2).map((ev) => (
                      <div key={ev.id} className={`h-1.5 rounded-full ${
                        ev.kind === "event" ? "bg-[#002FA7]" : ev.category === "teacher" ? "bg-purple-500" : "bg-amber-500"}`} />
                    ))}
                    {evs.length > 2 && <p className="text-[10px] text-slate-400">+{evs.length - 2}</p>}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-4 mt-4 text-xs text-slate-500 flex-wrap">
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#002FA7]" /> Event</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-500" /> Student deadline</span>
            {(user?.role === "admin" || user?.role === "teacher") && (
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-purple-500" /> Teacher deadline</span>
            )}
            <span className="flex items-center gap-1.5" data-testid="legend-holiday"><span className="h-2 w-2 rounded-full bg-red-600" /> Holiday / weekly off</span>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md" data-testid="events-list">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950">Upcoming</h2>
          </div>
          <div className="divide-y divide-slate-100 max-h-[480px] overflow-auto">
            {upcoming.length === 0 && (
              <div className="p-10 text-center" data-testid="events-empty">
                <CalendarDays className="h-8 w-8 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-sm text-slate-400">No events or deadlines yet.</p>
              </div>
            )}
            {upcoming.map((ev) => (
              <div key={ev.id} className="px-6 py-3.5 flex items-start justify-between gap-4" data-testid={`event-row-${ev.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm text-slate-950">{ev.title}</p>
                    <KindBadge kind={ev.kind} category={ev.category} />
                    {ev.scope && ev.scope !== "all" && (
                      <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200" data-testid={`event-scope-${ev.id}`}>
                        {ev.scope === "department" ? "Department" : "Semester"}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 font-data mt-1">{ev.date} · {ev.created_by_name}</p>
                  {ev.description && <p className="text-sm text-slate-600 mt-1">{ev.description}</p>}
                </div>
                {(ev.kind === "holiday" ? canHoliday : canManage) && (
                  <div className="flex gap-1 shrink-0">
                    {ev.kind !== "holiday" && (
                      <Button variant="ghost" size="icon" data-testid={`edit-event-${ev.id}`} onClick={() => openEdit(ev)}
                        className="text-slate-400 hover:text-slate-950 hover:bg-slate-100"><Pencil className="h-4 w-4" /></Button>
                    )}
                    <Button variant="ghost" size="icon" data-testid={`delete-event-${ev.id}`}
                      onClick={() => (ev.kind === "holiday" ? removeHoliday(ev.id) : remove(ev.id))}
                      className="text-slate-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white rounded-md" data-testid="event-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit entry" : canManage ? "Add event / deadline / holiday" : "Add holiday"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5"><Label>{form.kind === "holiday" ? "Holiday name" : "Title"}</Label>
              <Input required data-testid="event-title-input" value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Date</Label>
                <Input type="date" required data-testid="event-date-input" value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Type</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="event-kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="event">Event (visible to all)</SelectItem>
                    <SelectItem value="deadline">Deadline (category-wise)</SelectItem>
                    {canHoliday && <SelectItem value="holiday" data-testid="event-kind-holiday">Holiday (day off, shown in red)</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.kind === "deadline" && (
              <div className="space-y-1.5"><Label>Deadline for</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="event-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="student">Students (visible to all)</SelectItem>
                    <SelectItem value="teacher">Teachers (visible to teachers & admin only)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.kind === "holiday" ? (
              user?.role === "admin" ? (
                <div className="space-y-1.5"><Label>Holiday applies to</Label>
                  <Select value={form.scope === "department" ? form.department_id : "all"}
                    onValueChange={(v) => setForm({ ...form, scope: v === "all" ? "all" : "department", department_id: v === "all" ? "" : v, semester_id: "" })}>
                    <SelectTrigger data-testid="holiday-dept-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All departments</SelectItem>
                      {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <p className="text-xs text-slate-500" data-testid="holiday-hod-scope-note">This holiday applies to your department only.</p>
              )
            ) : (
              <>
            <div className="space-y-1.5"><Label>Applies to</Label>
              <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v, semester_id: "", department_id: user?.role === "admin" ? "" : user?.department_id || "" })}>
                <SelectTrigger data-testid="event-scope-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Everyone (all departments)</SelectItem>
                  <SelectItem value="department">Specific department</SelectItem>
                  <SelectItem value="semester">Specific semester</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.scope === "department" && user?.role === "admin" && (
              <div className="space-y-1.5"><Label>Department</Label>
                <Select value={form.department_id} onValueChange={(v) => setForm({ ...form, department_id: v })}>
                  <SelectTrigger data-testid="event-dept-select"><SelectValue placeholder="Select department" /></SelectTrigger>
                  <SelectContent>
                    {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.scope === "semester" && (
              <div className="space-y-1.5"><Label>Semester</Label>
                <Select value={form.semester_id} onValueChange={(v) => setForm({ ...form, semester_id: v })}>
                  <SelectTrigger data-testid="event-semester-select"><SelectValue placeholder="Select semester" /></SelectTrigger>
                  <SelectContent>
                    {(user?.role === "admin" ? semesters : teacherSubjects.map((s) => ({ id: s.semester_id, name: s.semester_name }))
                      .filter((s, i, arr) => arr.findIndex((x) => x.id === s.id) === i)
                    ).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
              </>
            )}
            {form.kind !== "holiday" && (
            <div className="space-y-1.5"><Label>Description (optional)</Label>
              <Textarea rows={2} data-testid="event-desc-input" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            )}
            <Button type="submit" data-testid="event-submit-btn" className="w-full bg-slate-950 hover:bg-slate-800 text-white">
              {editing ? "Save changes" : "Add"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={managersOpen} onOpenChange={setManagersOpen}>
        <DialogContent className="bg-white rounded-md" data-testid="managers-dialog">
          <DialogHeader><DialogTitle>Assign teachers to the events section</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Assigned teachers can add, edit and delete events & deadlines.</p>
          <div className="divide-y divide-slate-100 max-h-80 overflow-auto">
            {managers.map((t) => (
              <div key={t.id} className="py-3 flex items-center justify-between" data-testid={`manager-row-${t.id}`}>
                <div>
                  <p className="text-sm font-medium text-slate-950">{t.name}</p>
                  <p className="text-xs text-slate-400">{t.email}</p>
                </div>
                <Switch checked={t.event_manager} data-testid={`manager-toggle-${t.id}`}
                  onCheckedChange={(v) => toggleManager(t.id, v)} />
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
